import React, { useState, useRef, useEffect } from 'react';
import styles from './Accordion.module.css';
import AccordionItem from './AccordionItem';

const Accordion = ({ sections }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [alwaysExpanded, setAlwaysExpanded] = useState(true);
  const [runawayButtons, setRunawayButtons] = useState(false); // Состояние для убегающих кнопок
  const [escapedButtons, setEscapedButtons] = useState([]); // Массив ID кнопок, которые убежали
  const contentRefs = useRef(sections.map(() => React.createRef()));
  const navRefs = useRef(sections.map(() => React.createRef())); // Рефы для кнопок навигации

  // Переключение активной секции
  const toggleSection = (index) => {
    // Если режим убегающих кнопок включен, кнопка должна убежать вместо переключения секции
    if (runawayButtons) {
      makeButtonRunaway(index, null, true);
      return;
    }

    if (alwaysExpanded) {
      console.log('Все секции всегда раскрыты, переключение игнорируется');
      // Даже если секции всегда раскрыты, всё равно прокручиваем к выбранной
      scrollToSection(index);
      return;
    }
    setActiveSection(activeSection === index ? null : index);
    console.log(`Переключение раздела на: ${index}`);
    
    // Прокручиваем к секции
    scrollToSection(index);
  };
  
  // Функция для прокрутки к выбранной секции
  const scrollToSection = (index) => {
    const ref = contentRefs.current[index];
    if (ref && ref.current) {
      // Получаем текущую позицию элемента
      const yPosition = ref.current.getBoundingClientRect().top + window.pageYOffset;
      
      // Прокручиваем с учетом смещения в 300px сверху
      window.scrollTo({
        top: yPosition - 300,
        behavior: 'smooth'
      });
      
      console.log(`Прокрутка к секции ${index} со смещением 300px`);
    } else {
      console.log(`Не удалось найти ссылку на секцию ${index} для прокрутки`);
    }
  };

  // Счетчик нажатий клавиши
  const [keyPressCount, setKeyPressCount] = useState(0);
  const [lastKeyPressTime, setLastKeyPressTime] = useState(0);

  // Обработчик нажатия клавиши
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Проверяем, что нажата клавиша F или А (русская или английская)
      if (e.key.toLowerCase() === 'f' || e.key.toLowerCase() === 'а') {
        const currentTime = Date.now();
        
        // Сбрасываем счетчик если прошло больше 2 секунд с последнего нажатия
        if (currentTime - lastKeyPressTime > 2000) {
          setKeyPressCount(1);
        } else {
          setKeyPressCount(prev => prev + 1);
        }
        
        setLastKeyPressTime(currentTime);

        // Если достигнуто 5 нажатий, включаем режим убегающих кнопок
        if (keyPressCount + 1 === 5) {
          console.log('Активирован режим убегающих кнопок через комбинацию клавиш');
          setRunawayButtons(true);
          setKeyPressCount(0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [keyPressCount, lastKeyPressTime]);

  // Функция для скрытия header
  const hideHeader = () => {
    const header = document.querySelector('header');
    if (header) {
      header.style.display = 'none';
      console.log('Header скрыт');
    } else {
      console.log('Header не найден');
    }
  };

  // Функция для показа header
  const showHeader = () => {
    const header = document.querySelector('header');
    if (header) {
      header.style.display = '';
      console.log('Header показан');
    } else {
      console.log('Header не найден');
    }
  };

  // Сброс позиций всех кнопок
  const resetButtonPositions = () => {
    navRefs.current.forEach(ref => {
      if (ref && ref.current) {
        ref.current.style.position = '';
        ref.current.style.left = '';
        ref.current.style.top = '';
        ref.current.style.transition = '';
      }
    });
    console.log('Позиции всех кнопок сброшены');
  };

  // Функция для перемещения кнопки в случайное место на краю экрана
  const makeButtonRunaway = (index, event, isClick = false) => {
    if (!runawayButtons) return;
    
    // Если это событие наведения, проверяем, убегала ли кнопка ранее
    if (!isClick && !escapedButtons.includes(index)) {
      return; // Кнопка убегает только если на нее кликнули ранее или это клик
    }

    const button = navRefs.current[index]?.current;
    if (!button) return;
    
    // Получаем размеры окна
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Определяем, к какому краю экрана переместить кнопку
    // Выбираем случайный край (0 - верх, 1 - право, 2 - низ, 3 - лево)
    const edge = Math.floor(Math.random() * 4);
    
    let newX, newY;
    
    switch(edge) {
      case 0: // Верхний край
        newX = Math.floor(Math.random() * (windowWidth - button.offsetWidth));
        newY = -button.offsetHeight / 2;
        break;
      case 1: // Правый край
        newX = windowWidth - button.offsetWidth / 2;
        newY = Math.floor(Math.random() * (windowHeight - button.offsetHeight));
        break;
      case 2: // Нижний край
        newX = Math.floor(Math.random() * (windowWidth - button.offsetWidth));
        newY = windowHeight - button.offsetHeight / 2;
        break;
      case 3: // Левый край
        newX = -button.offsetWidth / 2;
        newY = Math.floor(Math.random() * (windowHeight - button.offsetHeight));
        break;
    }
    
    // Применяем новую позицию с анимацией
    button.style.position = 'fixed';
    button.style.transition = 'all 0.5s ease';
    button.style.left = `${newX}px`;
    button.style.top = `${newY}px`;
    button.style.zIndex = '10000';
    
    console.log(`Кнопка ${index} убежала на край экрана (${edge}): X:${newX}, Y:${newY}`);
    
    // Если это первый клик, добавляем кнопку в список убежавших
    if (isClick && !escapedButtons.includes(index)) {
      setEscapedButtons(prev => [...prev, index]);
    }
    
    // Предотвращаем стандартное поведение
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Обработчик для возврата кнопки при клике в режиме без убегания
  const handleButtonClick = (index, event) => {
    if (runawayButtons) {
      hideHeader();
      makeButtonRunaway(index, event, true);
    } else {
      toggleSection(index);
    }
  };

  // Эффект для восстановления header при размонтировании компонента
  useEffect(() => {
    // Функция очистки, которая выполнится при размонтировании
    return () => {
      if (runawayButtons) {
        showHeader();
        console.log('Header восстановлен при размонтировании компонента');
      }
    };
  }, [runawayButtons]);

  return (
    <div className={styles.accordion}>
      {/* Флажки для управления */}
      <div className={styles.expandControl}>
        {/* <label>
          <input 
            type="checkbox" 
            checked={alwaysExpanded} 
            onChange={handleAlwaysExpandedChange} 
          />
          <span>Раскрыть все секции</span>
        </label> */}
      </div>
      
      {/* Навигационные кнопки */}
      <div className={styles.navigation}>
        {sections.map((section, index) => (
          <button
            key={`nav-${index}`}
            ref={navRefs.current[index]}
            className={`${styles.navButton} ${styles.active}`}
            onClick={(e) => handleButtonClick(index, e)}
            onMouseEnter={(e) => makeButtonRunaway(index, e)}
          >
            {section.icon}
            <span className={styles.cardTitle}>
                {section.title}
            </span>
          </button>
        ))}
      </div>
      
      {/* Секции аккордеона */}
      <div className={styles.sectionsContainer}>
        {sections.map((section, index) => (
          <AccordionItem 
            key={`item-${index}`}
            item={section}
            isActive={true}
            index={index}
            totalItems={sections.length}
            onToggle={toggleSection}
            contentRef={contentRefs.current[index]}
          />
        ))}
      </div>
    </div>
  );
};

export default Accordion; 